import { fabric } from 'fabric';
import { ISOPAL } from 'stores/fabric2/IsoPal';
import { Vector2 } from 'vector_math.js';
import { IsoShape } from '@sophya/eyekia';
import { DEFAULT_CIRCLE, FabricCircle } from './FabricCircle';
import { FabricObjectBase, FabricObjectCallbacks } from './FabricObjectBase';
import { FabricStore2 } from './FabricStore2';

export type LineData = {
  start: Vector2;
  end: Vector2;
};
export class FabricZone extends FabricObjectBase<fabric.Circle> {
  constructor(
    store: FabricStore2,
    fabricObject: fabric.Circle,
    private schema: IsoShape,
    private color: string,
    callbacks?: FabricObjectCallbacks<FabricZone>,
  ) {
    super(store, fabricObject, callbacks);

    const center = new Vector2(this.fabricObject.left, this.fabricObject.top);
    const half = ISOPAL.iso_to_pal_2d(new Vector2(schema.scale.x * 0.5, schema.scale.y * 0.5));
    const start = new Vector2(center.x - half.x, center.y - half.y);
    const end = new Vector2(center.x + half.x, center.y + half.y);

    // create start point
    this.start = new FabricObjectBase<fabric.Circle>(
      this.store,
      new fabric.Circle({
        ...DEFAULT_CIRCLE,
        top: start.y,
        left: start.x,
        fill: this.color,
        name: `${this.id}-start`,
      }),
      {
        onMove: this.handleEndpointModify,
        onSelect: this.notifySelect,
        onDeselect: this.notifyDeselect,
      },
    );

    this.children.add(this.start);
    this.childrenToMove.add(this.start);

    // create end point
    this.end = new FabricObjectBase<fabric.Circle>(
      this.store,
      new fabric.Circle({
        ...DEFAULT_CIRCLE,
        top: end.y,
        left: end.x,
        fill: this.color,
        name: `${this.id}-end`,
      }),
      {
        onMove: this.handleEndpointModify,
        onSelect: this.notifySelect,
        onDeselect: this.notifyDeselect,
      },
    );

    this.children.add(this.end);
    this.childrenToMove.add(this.end);

    //redraw the lines
    this.refreshLines();
    this.handleDeselect();
  }

  handleSelect = () => {
    this.start.enable();
    this.end.enable();
    this.lines.map(val => (val.fabricObject.opacity = 1));
  };

  handleDeselect = () => {
    this.start.disable();
    this.end.disable();
    this.lines.map(val => (val.fabricObject.opacity = 0.5));
  };

  start: FabricCircle;
  end: FabricCircle;

  lines: FabricObjectBase<fabric.Line>[] = [];

  handleMove = () => {
    this.refreshLines();
  };

  handleEndpointModify = () => {
    this.callbacks?.onModify?.(this);
    this.refreshLines();
  };

  generateLineData = (): LineData[] => {
    const startPal = new Vector2(this.start.x, this.start.y);
    const endPal = new Vector2(this.end.x, this.end.y);
    const p1Iso = ISOPAL.pal_to_iso_2d(startPal);
    const p4Iso = ISOPAL.pal_to_iso_2d(endPal);
    const p2Iso = new Vector2(p1Iso.x, p4Iso.y);
    const p3Iso = new Vector2(p4Iso.x, p1Iso.y);
    const leftPal = ISOPAL.iso_to_pal_2d(p3Iso);
    const rightPal = ISOPAL.iso_to_pal_2d(p2Iso);

    const lineDatas: LineData[] = [];
    lineDatas.push({ start: leftPal.clone(), end: startPal.clone() });
    lineDatas.push({ start: startPal.clone(), end: rightPal.clone() });
    lineDatas.push({ start: rightPal.clone(), end: endPal.clone() });
    lineDatas.push({ start: endPal.clone(), end: leftPal.clone() });
    return lineDatas;
  };

  generateCorners = (): Vector2[] => {
    const startPal = new Vector2(this.start.x, this.start.y);
    const endPal = new Vector2(this.end.x, this.end.y);
    const p1Iso = ISOPAL.pal_to_iso_2d(startPal);
    const p4Iso = ISOPAL.pal_to_iso_2d(endPal);
    const p2Iso = new Vector2(p1Iso.x, p4Iso.y);
    const p3Iso = new Vector2(p4Iso.x, p1Iso.y);
    const leftPal = ISOPAL.iso_to_pal_2d(p3Iso);
    const rightPal = ISOPAL.iso_to_pal_2d(p2Iso);

    const points = [];
    points.push(startPal);
    points.push(leftPal);
    points.push(endPal);
    points.push(rightPal);

    return points;
  };

  generateDimensions = (): Vector2 => {
    const startPal = new Vector2(this.start.x, this.start.y);
    const endPal = new Vector2(this.end.x, this.end.y);
    const p1Iso = ISOPAL.pal_to_iso_2d(startPal);
    const p4Iso = ISOPAL.pal_to_iso_2d(endPal);

    const width = p4Iso.x - p1Iso.x;
    const height = p4Iso.y - p1Iso.y;

    const diameters = new Vector2(width, height);
    return diameters;
  };

  refreshLines = () => {
    const lineDatas = this.generateLineData();

    if (this.lines.length === 0) {
      for (const line of lineDatas) {
        const fabLine = new FabricObjectBase(
          this.store,
          new fabric.Line([line.start.x, line.start.y, line.end.x, line.end.y], {
            stroke: this.color,
            opacity: 1,
            selectable: false,
            evented: false,
            excludeFromExport: true,
          }),
        );

        this.lines.push(fabLine);
        this.children.add(fabLine);
        this.childrenToMove.add(fabLine);
      }
    } else {
      this.lines.map((line, index) => {
        const lineData = lineDatas[index];
        line.fabricObject.set({
          x1: lineData.start.x,
          y1: lineData.start.y,
          x2: lineData.end.x,
          y2: lineData.end.y,
        });
      });
    }
    // refresh center point
    const startPal = new Vector2(this.start.x, this.start.y);
    const endPal = new Vector2(this.end.x, this.end.y);
    this.x = (startPal.x + endPal.x) * 0.5;
    this.y = (startPal.y + endPal.y) * 0.5;
    this.pastLocation = new Vector2(this.x, this.y);
    this.fabricObject.setCoords();
  };

  toSchema = (relativeTo: Vector2): IsoShape => {
    const startIso = ISOPAL.pal_to_iso_2d(new Vector2(this.start.x, this.start.y).sub(relativeTo));
    const endIso = ISOPAL.pal_to_iso_2d(new Vector2(this.end.x, this.end.y).sub(relativeTo));

    return {
      shape: this.schema.shape,
      position: {
        x: (startIso.x + endIso.x) * 0.5,
        y: (startIso.y + endIso.y) * 0.5,
      },
      scale: {
        x: Math.abs(startIso.x - endIso.x),
        y: Math.abs(startIso.y - endIso.y),
      },
      rotationRad: 0,
    };
  };
}
